import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Search, Filter, Mail, Phone, Globe, FileText, X, Download, Loader2 } from "lucide-react";

const STATUS_COLORS = {
  "New": "bg-blue-500/20 text-blue-200 border-blue-400/30",
  "Contacted": "bg-cyan-500/20 text-cyan-200 border-cyan-400/30",
  "Follow-Up Needed": "bg-amber-500/20 text-amber-200 border-amber-400/30",
  "Proposal Sent": "bg-violet-500/20 text-violet-200 border-violet-400/30",
  "Approved": "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
  "Declined": "bg-rose-500/20 text-rose-200 border-rose-400/30",
  "Closed": "bg-white/10 text-white/60 border-white/20",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function AdvertisingInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [busy, setBusy] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status_filter = statusFilter;
      if (query.trim()) params.q = query.trim();
      const { data } = await api.get("/admin/advertising-inquiries", { params });
      setInquiries(data.inquiries || []);
      setStatuses(data.statuses || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, [statusFilter, query]);

  useEffect(() => { refresh(); }, [refresh]);

  const updateStatus = async (inquiry, status) => {
    try {
      const { data } = await api.patch(`/admin/advertising-inquiries/${inquiry.id}`, { status });
      setInquiries((arr) => arr.map((i) => i.id === data.id ? data : i));
      if (selected?.id === data.id) setSelected(data);
      toast.success(`Status updated to "${status}"`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const counts = statuses.reduce((acc, s) => {
    acc[s] = inquiries.filter((i) => i.status === s).length;
    return acc;
  }, {});

  return (
    <div data-testid="admin-advertising-inquiries" className="max-w-[1500px] mx-auto">
      {/* Status pills */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <button
          data-testid="adm-adv-filter-all"
          onClick={() => setStatusFilter("all")}
          className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${statusFilter === "all" ? "bg-white text-black border-white" : "bg-white/5 border-white/15 hover:bg-white/10 text-white/85"}`}
        >
          All <span className="opacity-60 ml-1">{inquiries.length}</span>
        </button>
        {statuses.map((s) => (
          <button
            key={s}
            data-testid={`adm-adv-filter-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            onClick={() => setStatusFilter(s)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${statusFilter === s ? "bg-white text-black border-white" : "bg-white/5 border-white/15 hover:bg-white/10 text-white/85"}`}
          >
            {s} <span className="opacity-60 ml-1">{counts[s] || 0}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="adm-adv-search"
            placeholder="Search contact or business name…"
            className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-white/40" />
        </div>
      </div>

      {/* Table */}
      <div className="ycn-glass rounded-2xl overflow-hidden border border-white/10">
        <div className="grid grid-cols-[1.4fr,1.4fr,1fr,1fr,140px,120px] items-center px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-white/50 border-b border-white/10 bg-white/5">
          <div>Contact</div>
          <div>Business</div>
          <div>Opportunity</div>
          <div>Submitted</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>

        {busy && inquiries.length === 0 && (
          <div className="p-8 text-center text-white/50 flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading inquiries…
          </div>
        )}
        {!busy && inquiries.length === 0 && (
          <div className="p-10 text-center text-white/50" data-testid="adm-adv-empty">
            No advertising inquiries yet. Submissions will appear here.
          </div>
        )}

        <div className="max-h-[62vh] overflow-y-auto">
          {inquiries.map((i) => (
            <div
              key={i.id}
              data-testid={`adm-adv-row-${i.id}`}
              className="grid grid-cols-[1.4fr,1.4fr,1fr,1fr,140px,120px] items-center px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition"
            >
              <div className="min-w-0">
                <div className="font-semibold truncate">{i.contact_name}</div>
                <div className="text-[11px] text-white/45 truncate">{i.email}</div>
              </div>
              <div className="min-w-0">
                <div className="truncate">{i.business_name}</div>
                <div className="text-[11px] text-white/45 truncate">{i.industry || "—"}</div>
              </div>
              <div className="text-xs text-white/75 truncate">{i.preferred_opportunity}</div>
              <div className="text-xs text-white/60 truncate">{formatDate(i.created_at)}</div>
              <div>
                <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold border ${STATUS_COLORS[i.status] || STATUS_COLORS["Closed"]}`}>
                  {i.status}
                </span>
              </div>
              <div className="flex justify-end">
                <button
                  data-testid={`adm-adv-view-${i.id}`}
                  onClick={() => setSelected(i)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold ycn-glass hover:bg-white/15 transition"
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <InquiryDetailDrawer
          inquiry={selected}
          statuses={statuses}
          onClose={() => setSelected(null)}
          onStatusChange={(s) => updateStatus(selected, s)}
        />
      )}
    </div>
  );
}

function InquiryDetailDrawer({ inquiry, statuses, onClose, onStatusChange }) {
  const [downloading, setDownloading] = useState(false);

  const downloadFile = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("ycn_token");
      const res = await fetch(`${API_BASE}/admin/advertising-inquiries/${inquiry.id}/file`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = inquiry.file_name || "advertising-attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "File download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl h-full overflow-y-auto bg-[#0a0a0c] border-l border-white/10 shadow-2xl"
        data-testid="adm-adv-drawer">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-[#0a0a0c]/95 backdrop-blur border-b border-white/10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Advertising Inquiry</div>
            <div className="font-display text-xl font-bold truncate">{inquiry.business_name}</div>
          </div>
          <button onClick={onClose} data-testid="adm-adv-drawer-close"
            className="h-9 w-9 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60">
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status changer */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2">Status</div>
            <div className="flex flex-wrap gap-2">
              {statuses.map((s) => {
                const active = inquiry.status === s;
                return (
                  <button
                    key={s}
                    data-testid={`adm-adv-status-btn-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    onClick={() => onStatusChange(s)}
                    disabled={active}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition ${active ? "bg-white text-black border-white cursor-default" : "bg-white/5 border-white/15 hover:bg-white/10 text-white/85"}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 text-[11px] text-white/50">
              Submitted {formatDate(inquiry.created_at)}
              {inquiry.updated_at ? ` · Updated ${formatDate(inquiry.updated_at)}` : ""}
            </div>
          </div>

          {/* Contact card */}
          <Section title="Contact">
            <Row label="Name" value={inquiry.contact_name} />
            <Row label="Business" value={inquiry.business_name} />
            <Row label="Email" value={inquiry.email} icon={Mail} link={`mailto:${inquiry.email}`} />
            {inquiry.phone && <Row label="Phone" value={inquiry.phone} icon={Phone} link={`tel:${inquiry.phone}`} />}
            {inquiry.website && <Row label="Website" value={inquiry.website} icon={Globe} link={inquiry.website} external />}
            {inquiry.social_handle && <Row label="Social" value={inquiry.social_handle} />}
            {inquiry.industry && <Row label="Industry" value={inquiry.industry} />}
          </Section>

          <Section title="Campaign">
            <Row label="Preferred Opportunity" value={inquiry.preferred_opportunity} />
            <Row label="What to advertise" value={inquiry.what_to_advertise} multiline />
            {inquiry.campaign_goals && <Row label="Campaign Goals" value={inquiry.campaign_goals} multiline />}
            {inquiry.target_audience && <Row label="Target Audience" value={inquiry.target_audience} multiline />}
            {inquiry.budget && <Row label="Budget" value={inquiry.budget} />}
            {inquiry.start_date && <Row label="Preferred Start Date" value={inquiry.start_date} />}
            {inquiry.campaign_length && <Row label="Campaign Length" value={inquiry.campaign_length} />}
            <Row label="Advertisement Creative Status" value={inquiry.creative_status || "—"} highlight />
          </Section>

          {inquiry.additional_details && (
            <Section title="Additional Details">
              <div className="whitespace-pre-wrap text-sm text-white/85 leading-relaxed">{inquiry.additional_details}</div>
            </Section>
          )}

          {inquiry.file_name && (
            <Section title="Attached File">
              <div className="flex items-center gap-3 p-3.5 bg-white/5 border border-white/10 rounded-2xl">
                <FileText size={18} className="text-white/60 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate" data-testid="adm-adv-file-name">{inquiry.file_name}</div>
                  <div className="text-[11px] text-white/50">
                    {inquiry.file_content_type} · {(inquiry.file_size / 1024).toFixed(1)} KB
                  </div>
                </div>
                <button onClick={downloadFile} disabled={downloading}
                  data-testid="adm-adv-file-download"
                  className="ycn-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white disabled:opacity-60 transition-all">
                  {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  Download
                </button>
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="ycn-glass rounded-2xl p-5">
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">{title}</div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Row({ label, value, icon: Icon, link, external, multiline, highlight }) {
  return (
    <div className="grid grid-cols-[130px,1fr] gap-3 text-sm">
      <div className="text-white/55 flex items-center gap-1.5">{Icon && <Icon size={12} />} {label}</div>
      <div className={`min-w-0 ${multiline ? "whitespace-pre-wrap leading-relaxed" : "truncate"} ${highlight ? "text-orange-200 font-semibold" : "text-white/90"}`}>
        {link ? (
          <a href={link} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}
            className="hover:text-white underline underline-offset-2 decoration-white/30">
            {value}
          </a>
        ) : value}
      </div>
    </div>
  );
}
